import { useQuery } from '@tanstack/react-query'
import { portalLanguagesData } from 'app/appSettings'
import { queryGetObject, queryGetObjects } from 'app/backend/Common'
import { FormPage } from 'app/backend/salesforce-maps/FormPage'
import { SurveyTranslation } from 'app/backend/salesforce-maps/SurveyTranslation'
import { transformOldSfProps } from 'app/utils/transformOldSfProps'
import { isNil } from 'lodash'
import pako from 'pako'
import { isValidJSON } from 'utils'
import { textFields } from '../sfData/sfSurvey'

interface UseFormProps {
  formId?: string
}

interface UseFormResult {
  data: Partial<FormPage> & {
    title: {
      portalTranslationId?: {
        id: string | null
        text: string
      }
    }
    translations: SurveyTranslation | undefined
  } | undefined
  isLoading: boolean
  error: unknown
}

/**
 * Hook for fetching form data
 */
export const useForm = (props: UseFormProps): UseFormResult => {
  const {
    formId: propFormId
  } = props

  // Get the form configuration if formId is not provided
  const { data: formConfig, error: formConfigError, isLoading: isLoadingFormConfig } = useQuery({
    queryKey: ['configuration', 'FORM_ORGANIZATION_DETAILS'],
    queryFn: async () => {
      const result = await queryGetObjects({
        type: 'Configuration',
        searchProps: {
          Name: 'FORM_ORGANIZATION_DETAILS'
        }
      })
      return result[0]
    },
    enabled: propFormId === undefined
  })

  const effectiveFormId = propFormId ?? formConfig?.value

  const { data: form, error: formError, isLoading: isLoadingForm } = useQuery({
    queryKey: ['form', effectiveFormId],
    queryFn: async () => {
      const result = await queryGetObject({
        type: 'FormPage',
        id: effectiveFormId ?? ''
      })
      return result
    },
    enabled: Boolean(effectiveFormId)
  })

  const titleTranslation = form?.titleTranslation

  const surveyTranslationQuery = useQuery({
    queryKey: ['survey-translation', titleTranslation],
    queryFn: async () => {
      const result = await queryGetObject({
        type: 'SurveyTranslation',
        id: titleTranslation ?? '',
        include: [{ base: 'Survey_Texts__r', select: textFields }]
      })
      return result
    },
    enabled: Boolean(titleTranslation)
  })

  const translations = surveyTranslationQuery.data

  const title = isNil(translations)
    ? Object.values(portalLanguagesData).reduce<{ portalTranslationId?: { id: string | null, text: string } }>((acc, langObj) => {
      const { editKey } = langObj
      acc[editKey] = {
        id: null,
        text: ''
      }
      return acc
    }, {})
    : Object.values(portalLanguagesData).reduce<{ portalTranslationId?: { id: string | null, text: string } }>((acc, langObj) => {
      const { editKey, surveyTextKey } = langObj
      const translation =
          translations?.surveyTexts?.records.find((rec) =>
            rec.Language__c.includes(surveyTextKey)
          )
      acc[editKey] = {
        id: translation?.Id ?? null,
        text: translation?.Text__c ?? ''
      }
      return acc
    }, {})

  const portalTranslation =
    translations?.surveyTexts?.records.find(
      (rec) => rec.Text_Is_Portal_Translation_Id__c
    )

  title.portalTranslationId = {
    id: portalTranslation?.Id ?? null,
    text: portalTranslation?.Text__c ?? ''
  }

  let toPass: FormPage | undefined

  if (isValidJSON(form?.data)) {
    toPass = JSON.parse(typeof form?.data === 'string' ? form.data : '') as FormPage
  } else if (typeof form?.data === 'string') {
    try {
      const gzip = Buffer.from(form.data ?? '', 'base64')

      const unzipped = pako.ungzip(gzip, { to: 'string' })
      const parsed = JSON.parse(unzipped) as FormPage
      toPass = transformOldSfProps(parsed) as FormPage
    } catch (error) {
      console.log(error)
    }
  }

  return {
    data: form != null
      ? {
          ...toPass,
          title,
          translations,
          holdsReusableElements: form.holdsReusableElements,
          objectsConnected: toPass?.objectsConnected,
          comments: form.comments,
          origin: form.origin,
          type: form.type,
          status: form.status,
          id: form.id
        }
      : undefined,
    isLoading: Boolean(isLoadingForm) || isLoadingFormConfig,
    error: formError ?? formConfigError
  }
}
